import {Component, Input, OnInit, ViewChildren} from '@angular/core';
import {ConfigurableDate} from '../../configurable-date/configurable-date.model';
import {ConfigurableDateService} from '../../configurable-date/configurable-date.service';
import {EstimationInput} from '../estimation-input/estimation-input.model';
import {EstimationResult} from '../estimation.result.model';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {EstimationService} from '../estimation.service';
import {Estimation} from '../estimation.model';
import {DWPValidatorsService} from '../../dwpvalidators.service';
import {EstimationInputComponent} from '../estimation-input/estimation-input.component';
import {SweetUtil} from '../../components/commons/sweet/sweet-util-service';

@Component({
  selector: 'app-estimation-details',
  templateUrl: './estimation-details.component.html',
  styleUrls: ['./estimation-details.component.css']
})
export class EstimationDetailsComponent implements OnInit {
  @Input()
  dealerNumber: string;
  @Input()
  contractTypes: Array<string>;
  @Input()
  displayOrderAndDeliveryDates: boolean;
  @Input()
  displayMileage: boolean;
  @Input()
  selectVehicleDetailsMethod: boolean;
  @ViewChildren(EstimationInputComponent)
  estimationInputComponents: Array<EstimationInputComponent>;

  form: UntypedFormGroup;
  estimationWithoutVinForm: UntypedFormGroup;

  configurableDate: ConfigurableDate;

  firstEstimationInput: EstimationInput;
  secondEstimationInput: EstimationInput;
  thirdEstimationInput: EstimationInput;
  forthEstimationInput: EstimationInput;
  fifthEstimationInput: EstimationInput;

  estimationResults: Array<EstimationResult>;
  today = new Date().toISOString().substr(0, 10);
  invalidDateErrorMessage = 'please-enter-a-valid-date';
  valueRequired = 'please-enter-a-value';

  displaySelectCarDetailsMethodButtons: any;
  estimateWithVin = true;
  durationToOptionMap = new Map([
    ['36', '07CG'],
    ['48', '07CH'],
  ]);

  constructor(private service: EstimationService, private validator: DWPValidatorsService, private sweetUtil: SweetUtil,
              private configurableDateService: ConfigurableDateService, private formBuilder: UntypedFormBuilder) {
    this.form = formBuilder.group({
      orderDate: new UntypedFormControl(''),
      deliveryDate: new UntypedFormControl(''),
      firstRegistrationDate: new UntypedFormControl(''),
      modelCode: new UntypedFormControl(''),
      option: new UntypedFormControl('')
    });
  }

  setupValidators() {
    if (this.selectVehicleDetailsMethod && !this.estimateWithVin) {
      this.form.get('modelCode').setValidators([this.validator.notBlank]);
      this.form.get('duration').setValidators([this.validator.notBlank]);
    } else {
      if (this.displayOrderAndDeliveryDates) {
        this.form.get('orderDate').setValidators([this.validator.date]);
        this.form.get('deliveryDate').setValidators([this.validator.date]);
      } else {
        this.form.get('orderDate').clearValidators();
        this.form.get('deliveryDate').clearValidators();
      }
    }
  }

  ngOnInit(): void {
    this.setupValidators();
    this.configurableDateService.configurableDates().subscribe((configurableDates: Array<ConfigurableDate>) => {
      this.configurableDate = configurableDates.find((date: ConfigurableDate) => {
        return date.type.toUpperCase() === 'SAVING';
      });
    });
  }

  calculatable(): boolean {
    const calculatable: boolean = this.firstEstimationInput &&
      this.firstEstimationInput.valid &&
      (this.displayOrderAndDeliveryDates && this.form.valid || !this.displayOrderAndDeliveryDates);
    return calculatable;
  }


  onFirstEstimationInput(estimationInput: EstimationInput): void {
    this.firstEstimationInput = estimationInput;
  }

  onSecondEstimationInput(estimationInput: EstimationInput): void {
    this.secondEstimationInput = estimationInput;
  }

  onThirdEstimationInput(estimationInput: EstimationInput): void {
    this.thirdEstimationInput = estimationInput;
  }

  onForthEstimationInput(estimationInput: EstimationInput): void {
    this.forthEstimationInput = estimationInput;
  }

  onFifthEstimationInput(estimationInput: EstimationInput): void {
    this.fifthEstimationInput = estimationInput;
  }

  estimateWithNoVin() {
    const estimationRequests: Array<Estimation> = [];
    estimationRequests.push(this.estimationWithNoVin());
    this.doEstimate(estimationRequests);
  }

  estimate(): void {
    const estimationRequests: Array<Estimation> = [];
    if (this.firstEstimationInput) {
      estimationRequests.push(this.estimation(this.firstEstimationInput));
    }

    if (this.secondEstimationInput) {
      estimationRequests.push(this.estimation(this.secondEstimationInput));
    }

    if (this.thirdEstimationInput) {
      estimationRequests.push(this.estimation(this.thirdEstimationInput));
    }
    if (this.forthEstimationInput) {
      estimationRequests.push(this.estimation(this.forthEstimationInput));
    }
    if (this.fifthEstimationInput) {
      estimationRequests.push(this.estimation(this.fifthEstimationInput));
    }
    if (this.firstEstimationInput.valid) {
      this.doEstimate(estimationRequests);
    }
  }

  private doEstimate(estimationRequests: Array<Estimation>) {
    if (estimationRequests.length > 0) {
      this.service.estimate(estimationRequests).subscribe((body: Array<EstimationResult>) => {
        this.estimationResults = body;
      }, (error) => {
        this.sweetUtil.showError(error.message);
      });
    }
  }

  private estimationWithNoVin(): Estimation {
    const modelCode: string = this.form.get('modelCode').value;
    const firstRegistration: Date = this.form.get('firstRegistrationDate').value;
    const duration: string = this.form.get('duration').value;
    const option: string = this.durationToOptionMap.get(duration);
    return {
      firstRegistration,
      modelCode,
      duration,
      option,
      dealerNumber: this.dealerNumber,
      contractTypes: this.contractTypes,
    };
  }

  private estimation(estimation: EstimationInput): Estimation {
    const orderDate: Date = this.form.get('orderDate').value;
    const deliveryDate: Date = this.form.get('deliveryDate').value;
    const modelCode: string = this.form.get('modelCode').value;
    return {
      orderDate,
      deliveryDate,
      modelCode,
      option: null,
      duration: null,
      dealerNumber: this.dealerNumber,
      contractTypes: this.contractTypes,
      vin: estimation.vin,
      mileage: estimation.mileage,
      firstRegistration: estimation.firstRegistration
    };
  }

  clearFields(): void {
    this.setupForm();
    this.estimationInputComponents.forEach((estimationInputComponent: EstimationInputComponent) => {
      estimationInputComponent.clearFields();
    });
    this.estimationResults = null;
    this.fifthEstimationInput = null;
    this.forthEstimationInput = null;
    this.thirdEstimationInput = null;
    this.secondEstimationInput = null;
    this.firstEstimationInput = null;
    this.estimationResults = null;
    this.setupValidators();
  }

  setupForm() {
    this.form = this.formBuilder.group({
      orderDate: new UntypedFormControl(''),
      deliveryDate: new UntypedFormControl(''),
      firstRegistrationDate: new UntypedFormControl(''),
      modelCode: new UntypedFormControl(''),
      duration: new UntypedFormControl('')
    });
  }

  onSelectCarDetailsMethod(method: string) {
    if (method === 'vin') {
      this.estimateWithVin = true;
    }
    if (method === 'novin') {
      this.estimateWithVin = false;
    }
    this.clearFields();
    this.setupValidators();
  }
}
